import { forwardRef, type InputHTMLAttributes, useId } from 'react'
import { Check } from 'lucide-react'

/* ==========================================================================
   Checkbox Component
   Based on design.md specifications
   Task F30: Updated to use pharmacy colors for checked/focus states
   ========================================================================== */

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** Label text displayed next to checkbox */
  label?: string
  /** Error state styling */
  error?: boolean
}

/**
 * Checkbox component with custom styling and optional label.
 * Supports all standard input attributes.
 *
 * @example
 * ```tsx
 * <Checkbox label="Remember me" />
 * <Checkbox label="I agree to the terms" required />
 * <Checkbox checked disabled />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error = false, disabled, className = '', id, ...props }, ref) => {
    const generatedId = useId()
    const checkboxId = id ?? generatedId

    const baseCheckboxStyles = [
      // Layout
      'peer',
      'h-5 w-5',
      'shrink-0',
      // Appearance
      'appearance-none',
      'rounded',
      'border',
      'bg-white',
      // Transitions
      'transition-all duration-150 ease-in-out',
      // Focus state - uses pharmacy secondary color (Task F30)
      'focus:outline-none',
      'focus-visible:ring-3 focus-visible:ring-[var(--pharmacy-secondary)]',
      // Checked state - uses pharmacy primary color (Task F30)
      'checked:bg-[var(--pharmacy-primary)]',
      'checked:border-[var(--pharmacy-primary)]',
    ]

    const stateStyles = error
      ? ['border-[var(--color-error)]', 'focus:border-[var(--color-error)]']
      : [
          // Uses pharmacy primary color for hover (Task F30)
          'border-[var(--color-gray-300)]',
          'hover:border-[var(--pharmacy-primary)]',
        ]

    const disabledStyles = disabled
      ? [
          'bg-[var(--color-gray-100)]',
          'cursor-not-allowed',
          'checked:bg-[var(--color-gray-400)]',
          'checked:border-[var(--color-gray-400)]',
        ]
      : ['cursor-pointer']

    const checkboxClasses = [
      ...baseCheckboxStyles,
      ...stateStyles,
      ...disabledStyles,
    ]
      .filter(Boolean)
      .join(' ')

    const labelStyles = [
      'text-sm',
      'text-[var(--color-charcoal)]',
      'select-none',
      disabled
        ? 'text-[var(--color-gray-500)] cursor-not-allowed'
        : 'cursor-pointer',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <label
        htmlFor={checkboxId}
        className={`inline-flex items-center gap-2 ${className}`}
      >
        <span className="relative inline-flex items-center justify-center">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            disabled={disabled}
            className={checkboxClasses}
            aria-invalid={error || undefined}
            {...props}
          />
          {/* Checkmark icon - hidden by default, visible when checked via peer */}
          <Check
            className="absolute h-3.5 w-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
            strokeWidth={3}
            aria-hidden="true"
          />
        </span>
        {label && <span className={labelStyles}>{label}</span>}
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
